import React, { useEffect, useRef, useState } from 'react';
import { Link as ReactRouterDOMLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { css } from 'emotion';
import styled from '@emotion/styled';
import moment from 'moment';
import { LoadingIndicator } from '@spotify-internal/encore-web';

import { AnchorAPI } from 'modules/AnchorAPI';
import { ContactabilityStatus } from 'modules/AnchorAPI/contactability/fetchContactabilityLocationRule';
import { dayOptions, monthOptions, yearOptions } from 'modules/Date/constants';
import { debounce } from 'modules/debounce';
import { Button } from 'shared/Button/NewButton';
import { LinkText } from 'shared/Link';
import { Text } from 'shared/Text';
import Box from 'shared/Box';
import { ControlledCaptcha } from 'components/Captcha';
import { FieldCheckbox, FieldInput } from 'components/Field';
import { FieldSelect } from 'components/FieldSelect';
import { FormErrorAlert } from 'components/FormErrorAlert';
import events from 'components/SignupPageContainer/events';
import { fetchIsVanitySlugAvailable } from 'client/onboarding';
import {
  BIRTHDATE_ERROR_MESSAGE,
  EXISTING_ACCOUNT_ERROR_MESSAGE,
  EXISTING_EMAIL_ERROR_MESSAGE,
  isValidEmail,
  isValidPassword,
  PASSWORD_ERROR_MESSAGE,
} from 'client/user';
import { paths } from 'client/shared/Paths';

const FieldsContainer = styled.div`
  width: 100%;
  max-width: 410px;
  margin: auto;
  overflow: hidden;
`;

const FieldWrapper = styled.div`
  margin-bottom: 10px;
`;

const DobWrapper = styled.div`
  margin: 15px 0 10px;

  > label {
    margin-bottom: 8px;
  }
`;

const FlexFields = styled.div`
  display: flex;

  > * {
    flex: 1;
    &:first-of-type {
      flex: 1.5;
    }
    &:not(:first-of-type) {
      margin-left: 10px;
    }
  }
`;

const DateSelect = styled(FieldSelect)`
  font-size: 1.6rem;
`;

const CatpchaWrapper = styled.div`
  margin-top: 24px;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  max-width: 300px;
  margin: 32px auto 14px;
`;

const Terms = styled.div`
  margin-bottom: 21px;
  color: #7f8287;
  font-weight: normal;
  font-size: 11px;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
`;

type Data = {
  name: string;
  email: string;
  birthdate?: string;
  password: string;
  hasOptedIntoContactability: boolean;
  captcharesponse: string;
  vanitySlug?: string;
};

export type SignupFormProps = {
  feedUrl?: string;
  onSubmit: (data: Data) => Promise<void>;
  invalid: boolean;
  onClickRequestResetPassword: () => void;
  isLoading?: boolean;
  isV3Captcha?: boolean;
  loginUrl?: string;
  isImporting: boolean;
  isWordPressReferral: boolean;
  vanitySlug?: string;
};

export const SignupForm = ({
  feedUrl = '',
  onSubmit,
  onClickRequestResetPassword,
  isLoading = false,
  isV3Captcha = false,
  loginUrl,
  isImporting,
  isWordPressReferral,
  vanitySlug,
}: SignupFormProps) => {
  const {
    handleSubmit,
    formState: { errors, isSubmitting, dirtyFields },
    register,
    control,
    getValues,
    setValue,
    trigger,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      vanitySlug,
      name: '',
      email: '',
      password: '',
      dobMonth: '',
      dobDay: '',
      dobYear: '',
      captcharesponse: '',
      hasOptedIntoContactability: false,
    },
    shouldFocusError: true,
  });

  const [showContactCheckbox, setShowContactCheckbox] = useState(false);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const { current: debouncedOnVanityChange } = useRef(
    debounce(async () => {
      await trigger('vanitySlug');
    }, 500)
  );

  // If we receive new vanity slug prop, use it. This is for when ImportCreateAccount
  // fetches vanitySlug for the first time and asynchronously passes the prop here.
  useEffect(() => {
    setValue('vanitySlug', vanitySlug);
  }, [setValue, vanitySlug]);

  const [isVanityLoading, setVanityLoading] = useState(false);

  /**
   * Sets the `hasOptedIntoContactability` checkbox value
   * Hides/renders the checkbox based on user location contactability rules
   */
  useEffect(() => {
    AnchorAPI.fetchContactabilityLocationRule()
      .then(({ countryRule }) => {
        const defaultCheckboxValue =
          countryRule === ContactabilityStatus.OPTED_IN ||
          countryRule === ContactabilityStatus.OPTED_IN_SECONDARY_RESPONSE ||
          countryRule === ContactabilityStatus.NEEDS_RESPONSE_OPT_IN;
        setValue('hasOptedIntoContactability', defaultCheckboxValue);
        if (countryRule && countryRule !== ContactabilityStatus.OPTED_IN) {
          setShowContactCheckbox(true);
        }
      })
      .catch(err => {
        throw new Error(err.message);
      });
  }, [setValue]);

  const trackClickSignUp = () => {
    events.signUpRegisterButtonClicked();
  };

  const dobError = errors.dobDay || errors.dobMonth || errors.dobYear;
  const isSubmitDisabled = isLoading || isSubmitting;

  const finalLoginUrl =
    loginUrl ||
    `/login${feedUrl ? `?feedUrl=${encodeURIComponent(feedUrl)}` : ''}`;

  const vanityRegistration = vanitySlug
    ? register('vanitySlug', {
        validate: {
          checkUnique: async data => {
            setVanityLoading(true);
            const result = await fetchIsVanitySlugAvailable(data);
            setVanityLoading(false);
            return result;
          },
        },
      })
    : undefined;

  return (
    <form
      onSubmit={
        !isSubmitDisabled
          ? handleSubmit(({ dobDay, dobMonth, dobYear, ...rest }) => {
              const data = {
                birthdate: `${dobYear}-${dobMonth}-${dobDay}`,
                ...rest,
              };
              // Clear any exisiting error message
              setErrorMessage(null);
              // Make sure to resolve promise so isSubmitting is accurate
              return onSubmit(data).catch(({ message }) =>
                setErrorMessage(message)
              );
            })
          : () => {}
      }
      id="SignupForm"
      method="POST"
      noValidate={true}
    >
      <FieldsContainer>
        {vanityRegistration && (
          <>
            <div
              className={css`
                display: flex;
                align-items: center;
              `}
            >
              <p>
                <strong>anchor.fm/</strong>
              </p>
              <FieldInput
                {...vanityRegistration}
                name="vanitySlug"
                type="text"
                onChange={e => {
                  vanityRegistration.onChange(e);
                  setVanityLoading(true);
                  // Trigger the async validation
                  debouncedOnVanityChange();
                }}
                containerClassName={css`
                  flex-grow: 1;
                `}
              />
            </div>
            <div
              className={css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                margin: 4px 0 6px 0;
              `}
            >
              {isVanityLoading ? (
                <LoadingIndicator indicatorSize={LoadingIndicator.sm} />
              ) : !dirtyFields.vanitySlug ? (
                <p
                  className={css`
                    width: 100%;
                    color: #5f6369;
                    text-align: right;
                    opacity: 0.5;
                  `}
                >
                  Tip: you can customize this URL.
                </p>
              ) : !errors.vanitySlug ? (
                <p
                  className={css`
                    color: #27de56;
                  `}
                >
                  Looks good!
                </p>
              ) : (
                <p
                  className={css`
                    color: #ff6663;
                  `}
                >
                  Sorry, that URL is unavailable.
                </p>
              )}
            </div>
          </>
        )}
        <FieldWrapper>
          <FieldInput
            {...register('name', {
              required: 'Name is required',
            })}
            error={errors.name}
            placeholder="Full Name"
            type="text"
            autoComplete="name"
            aria-label="name"
            autoFocus={true}
          />
        </FieldWrapper>
        <FieldWrapper>
          <FieldInput
            {...register('email', {
              required: 'Email is required',
              validate: email => isValidEmail(email) || 'Email is invalid',
            })}
            error={errors.email}
            placeholder="Email"
            type="email"
            autoComplete="email"
            aria-label="email"
          />
        </FieldWrapper>
        <FieldWrapper>
          <FieldInput
            {...register('password', {
              required: 'Password is required',
              validate: {
                minLength: pw =>
                  pw.length >= 7 || 'Passwords must be 7 characters or more',
                valid: pw => isValidPassword(pw) || PASSWORD_ERROR_MESSAGE,
              },
            })}
            error={errors.password}
            placeholder="Password"
            type="password"
            autoComplete="new-password"
            aria-label="password"
          />
        </FieldWrapper>
        <DobWrapper>
          {/** Ignoring eslint because it is not picking up the htmlFor and control id */}
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="dobMonth">What’s your date of birth?</label>
          <FlexFields>
            <DateSelect
              {...register('dobMonth', {
                required: true,
              })}
              id="dobMonth"
              options={monthOptions}
              isShowingError={!!errors.dobMonth}
              placeholder="Month"
              aria-label="Birthday month"
              required
            />
            <DateSelect
              {...register('dobDay', {
                required: true,
                validate: day => {
                  const month = getValues('dobMonth');
                  const year = getValues('dobYear');
                  if (!month || !year) return true;
                  // Ensure date is valid (e.g. not Feb 31)
                  return (
                    moment(`${year}-${month}-${day}`).isValid() ||
                    'Date is not possible'
                  );
                },
              })}
              id="dobDay"
              options={dayOptions}
              isShowingError={!!errors.dobDay}
              placeholder="Day"
              aria-label="Birthday date"
              required
            />
            <DateSelect
              {...register('dobYear', {
                required: true,
              })}
              id="dobYear"
              options={yearOptions}
              isShowingError={!!errors.dobYear}
              placeholder="Year"
              aria-label="Birthday year"
              required
            />
          </FlexFields>
          {dobError && (
            <div
              className={css`
                margin-top: 4px;
                color: #d0021b;
                font-size: 1.4rem;
                line-height: 1.8rem;
              `}
              id="dob-error"
            >
              {errors.dobDay?.message || 'Date of birth is required'}
            </div>
          )}
        </DobWrapper>
        <CatpchaWrapper>
          <ControlledCaptcha
            control={control}
            name="captcharesponse"
            isV3={isV3Captcha}
            rules={{
              required: 'Please complete the reCAPTCHA',
            }}
          />
        </CatpchaWrapper>
      </FieldsContainer>
      {errorMessage && (
        <Box display="flex" justifyContent="center">
          <Box maxWidth={404} width="100%">
            {(() => {
              switch (errorMessage) {
                case EXISTING_ACCOUNT_ERROR_MESSAGE:
                  return (
                    <FormErrorAlert
                      title="Existing account found"
                      renderMessage={
                        <>
                          We found an existing account that uses these
                          credentials. You can{' '}
                          <ReactRouterDOMLink
                            to={finalLoginUrl}
                            onClick={() => events.signUpSignInButtonClicked()}
                          >
                            log in
                          </ReactRouterDOMLink>{' '}
                          ‌or try again with a different email address.
                        </>
                      }
                    />
                  );
                case EXISTING_EMAIL_ERROR_MESSAGE:
                  return (
                    <FormErrorAlert
                      title="This email address is already being used"
                      renderMessage={
                        <>
                          We found an existing account with this email address,
                          but this password doesn’t match. You can try again or{' '}
                          <ReactRouterDOMLink
                            to={finalLoginUrl}
                            onClick={onClickRequestResetPassword}
                          >
                            reset your password
                          </ReactRouterDOMLink>
                          .
                        </>
                      }
                    />
                  );
                case BIRTHDATE_ERROR_MESSAGE:
                  return <FormErrorAlert title={BIRTHDATE_ERROR_MESSAGE} />;
                default:
                  return (
                    <FormErrorAlert
                      title="Unable to create account"
                      renderMessage={
                        <>
                          Sorry, but we were unable to create your account.
                          Please refresh the page and try again, or visit{' '}
                          <LinkText
                            to={paths.getHelpPage('home')}
                            isInline={true}
                          >
                            {paths.getHelpPage('home')}
                          </LinkText>{' '}
                          so we can help you troubleshoot.
                        </>
                      }
                    />
                  );
              }
            })()}
          </Box>
        </Box>
      )}
      <ButtonWrapper>
        <Button
          className={css`
            width: 100%;
          `}
          onClick={trackClickSignUp}
          type="submit"
          color="purple"
          isDisabled={isSubmitDisabled}
        >
          Sign up
        </Button>
      </ButtonWrapper>
      <Terms>
        {showContactCheckbox && (
          <FieldCheckbox
            {...register('hasOptedIntoContactability')}
            type="checkbox"
            label="Please send me news and offers from Anchor"
            containerClassName={css`
              margin-top: 17px;
              margin-bottom: 14px;
            `}
          />
        )}
        {!isWordPressReferral && (
          <LoginImportLink
            isImporting={isImporting}
            finalLoginUrl={finalLoginUrl}
          />
        )}
        <div
          className={css`
            margin-top: 32px;
            color: #292f36;
          `}
        >
          By continuing, you agree to our{' '}
          <LinkText
            target="_blank"
            onClick={() => events.signUpLegalLinkClicked({ link: 'tos' })}
            isInline={true}
            to={paths.legal.tos}
            color="gray"
          >
            Terms of Service
          </LinkText>{' '}
          and{' '}
          <LinkText
            target="_blank"
            onClick={() => events.signUpLegalLinkClicked({ link: 'privacy' })}
            isInline={true}
            color="gray"
            to={paths.legal.privacy}
          >
            Privacy Policy
          </LinkText>
          .
        </div>
      </Terms>
    </form>
  );
};

function LoginImportLink({
  isImporting,
  finalLoginUrl,
}: Pick<SignupFormProps, 'isImporting'> & { finalLoginUrl: string }) {
  return isImporting ? (
    <div
      className={css`
        margin-bottom: 32px;
      `}
    >
      <Text size="md" align="center">
        Already have an account?{' '}
        <Text size="md" isBold={true} isInline={true}>
          <ReactRouterDOMLink to={finalLoginUrl}>Log in</ReactRouterDOMLink>{' '}
        </Text>
        instead.
      </Text>
    </div>
  ) : (
    <p
      className={css`
        font-size: 1.4rem;
        text-align: center;
      `}
    >
      You can also{' '}
      <ReactRouterDOMLink
        data-cy="importLink"
        to={paths.anchor.switchForm}
        onClick={events.signUpImportButtonClicked}
      >
        import
      </ReactRouterDOMLink>{' '}
      an existing podcast.
    </p>
  );
}
